import {
    LandingBlockItemV2,
    LandingBlockV2,
    LandingProjectV2,
    LandingTemplateV2Key,
    createLandingProjectFromTemplate,
    isLandingProjectV2,
    landingTemplateByKey
} from '../models/lead-landing-project.model';
import {
    LeadLandingFormConfig,
    LeadLandingFormField,
    LeadLandingPageDto,
    PublicLeadLandingPageDto
} from '../models/lead-link.model';

export const AURORA_FORM_SLOT = '<div class="lp-form-slot" data-aurora-form-slot></div>';

export function extractLandingProjectV2(value: unknown): LandingProjectV2 | null {
    if (isLandingProjectV2(value)) return value;
    return null;
}

export function projectFromLandingPage(page: LeadLandingPageDto | PublicLeadLandingPageDto): LandingProjectV2 | null {
    const project = extractLandingProjectV2(page.content?.editor?.projectData);
    if (project) return project;

    const templateKey = normalizeTemplateV2Key(page.templateKey);
    const template = landingTemplateByKey(templateKey);
    const fallback = createLandingProjectFromTemplate(templateKey, {
        brandName: page.name,
        logoUrl: page.brand?.logoUrl || undefined,
        primaryColor: page.brand?.primaryColor || '#3157e6',
        accentColor: page.brand?.accentColor || '#16b981',
        backgroundColor: '#f7f9fc',
        textColor: '#0f172a',
        fontFamily: page.brand?.fontFamily || 'Inter, system-ui, sans-serif',
        tone: 'Chiaro e professionale',
        source: 'tenant'
    });
    const hero = fallback.blocks.find((block) => block.type === 'hero');
    if (hero) {
        hero.eyebrow = page.content?.eyebrow || hero.eyebrow;
        hero.title = page.content?.headline || hero.title;
        hero.body = page.content?.subtitle || page.content?.body || hero.body;
        hero.ctaLabel = page.content?.ctaLabel || page.form?.submitLabel || hero.ctaLabel;
        hero.imageUrl = page.content?.heroImageUrl || hero.imageUrl;
    }
    fallback.name = page.name || template.defaultName;
    fallback.slug = page.slug || fallback.slug;
    fallback.form = page.form || fallback.form;
    fallback.success = {
        title: page.success?.title || fallback.success.title,
        message: page.success?.message || fallback.success.message
    };
    return fallback;
}

export function normalizeTemplateV2Key(value: string | null | undefined): LandingTemplateV2Key {
    if (value === 'offer' || value === 'offer-band') return 'offer';
    if (value === 'event' || value === 'event-focus') return 'event';
    if (value === 'lead-magnet') return 'lead-magnet';
    if (value === 'consultation' || value === 'consulting') return 'consultation';
    return 'consultation';
}

export function renderLandingProjectHtml(project: LandingProjectV2, formMode: 'slot' | 'preview' = 'slot'): string {
    const formHtml = formMode === 'preview' ? renderPreviewForm(project.form) : AURORA_FORM_SLOT;
    const visibleBlocks = project.blocks.filter((block) => block.visible);
    const htmlBlocks = visibleBlocks.map((block) => renderBlock(project, block, formHtml)).join('\n');
    return `<main class="lp-page lp-page--${escapeAttribute(project.templateKey)}">
  ${renderBrandHeader(project)}
  ${htmlBlocks}
</main>`;
}

export function renderLandingProjectCss(project: LandingProjectV2): string {
    const theme = project.theme;
    return `
:root {
  --lp-primary: ${safeColor(theme.primaryColor, '#3157e6')};
  --lp-accent: ${safeColor(theme.accentColor, '#16b981')};
  --lp-bg: ${safeColor(theme.backgroundColor, '#f7f9fc')};
  --lp-ink: ${safeColor(theme.textColor, '#0f172a')};
}
* { box-sizing: border-box; }
html, body { margin: 0; min-height: 100%; }
body { background: var(--lp-bg); color: var(--lp-ink); font-family: ${safeFont(theme.fontFamily)}; }
img { max-width: 100%; height: auto; display: block; }
.lp-page { width: 100%; max-width: none; min-height: 100vh; background: linear-gradient(180deg, color-mix(in srgb, var(--lp-primary) 7%, var(--lp-bg)), var(--lp-bg) 38%); color: var(--lp-ink); overflow-x: hidden; }
.lp-brand { width: min(1120px, calc(100% - 32px)); margin: 0 auto; padding: 24px 0 6px; display: flex; align-items: center; justify-content: flex-start; gap: 14px; }
.lp-logo { max-width: 180px; max-height: 64px; object-fit: contain; }
.lp-logo-fallback { width: 44px; height: 44px; border-radius: 8px; display: grid; place-items: center; background: #fff; border: 1px solid color-mix(in srgb, var(--lp-primary) 22%, #dbe3ef); color: var(--lp-primary); font-weight: 700; }
.lp-brand-name { color: var(--lp-ink); font-size: 15px; line-height: 1.2; font-weight: 750; }
.lp-brand-row { display: flex; align-items: center; gap: 14px; margin-bottom: var(--lp-space-md); }
.lp-brand-logo { width: auto; max-width: 180px; max-height: 64px; object-fit: contain; }
.lp-brand-row:has(.lp-brand-logo) .lp-brand-name { display: none; }
.lp-section { width: 100%; max-width: none; margin: 0; padding: 56px 0; }
.lp-section-inner { width: min(1120px, calc(100% - 32px)); margin: 0 auto; }
.lp-section-media { margin-top: var(--lp-space-md); border-radius: var(--lp-radius); overflow: hidden; border: 1px solid #dbe3ef; background: #fff; }
.lp-section-media img { width: 100%; height: auto; min-height: 220px; max-height: 420px; object-fit: cover; display: block; }
.lp-hero { min-height: 70vh; display: grid; grid-template-columns: minmax(0, .96fr) minmax(310px, .64fr); gap: 42px; align-items: center; padding-top: 38px; }
.lp-hero-copy { display: grid; gap: 18px; align-content: center; }
.lp-kicker, .lp-section-kicker { width: fit-content; margin: 0; border: 1px solid color-mix(in srgb, var(--lp-primary) 28%, #dbe3ef); border-radius: 999px; background: #fff; color: color-mix(in srgb, var(--lp-primary) 86%, #0f172a); padding: 7px 12px; font-size: 13px; line-height: 1; font-weight: 650; }
.lp-hero h1 { margin: 0; color: var(--lp-ink); font-size: clamp(40px, 7vw, 72px); line-height: .98; letter-spacing: 0; font-weight: 680; text-wrap: balance; }
.lp-hero p, .lp-section-lede { margin: 0; color: color-mix(in srgb, var(--lp-ink) 78%, #64748b); font-size: clamp(17px, 2vw, 22px); line-height: 1.5; max-width: 62ch; }
.lp-button { width: fit-content; min-height: 46px; display: inline-flex; align-items: center; justify-content: center; border-radius: 8px; padding: 0 18px; background: var(--lp-primary); color: #fff; font-weight: 700; text-decoration: none; }
.lp-visual { min-height: 340px; border-radius: 12px; overflow: hidden; background: #0f172a; color: #fff; display: grid; align-content: end; gap: 14px; padding: 28px; box-shadow: 0 24px 70px rgba(15, 23, 42, .14); }
.lp-visual img { width: 100%; height: min(46vw, 420px); min-height: 260px; object-fit: cover; border-radius: 8px; }
.lp-visual strong { font-size: clamp(26px, 4vw, 42px); line-height: 1.05; font-weight: 680; }
.lp-visual span { color: color-mix(in srgb, var(--lp-accent) 78%, #fff); font-size: 13px; font-weight: 700; }
.lp-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; }
.lp-card { border: 1px solid #dbe3ef; border-radius: 8px; background: rgba(255,255,255,.9); padding: 20px; display: grid; gap: 10px; align-content: start; min-height: 138px; }
.lp-card strong, .lp-step strong, .lp-step h3, .lp-faq h3 { color: var(--lp-ink); font-size: 18px; line-height: 1.25; font-weight: 700; margin: 0; }
.lp-card p, .lp-step p, .lp-faq p, .lp-content p { margin: 0; color: color-mix(in srgb, var(--lp-ink) 72%, #64748b); line-height: 1.55; }
.lp-form-section { width: 100%; padding: 56px 0; border-top: 1px solid #dbe3ef; background: color-mix(in srgb, var(--lp-primary) 4%, var(--lp-bg)); }
.lp-form-section .lp-section-inner { display: grid; grid-template-columns: minmax(0, 1fr) minmax(300px, 440px); gap: 34px; align-items: start; }
.lp-form-copy { display: grid; gap: 14px; align-content: start; }
.lp-form-section [data-aurora-form-slot] { width: 100%; max-width: 100%; min-width: 0; align-self: start; }
.lp-split { display: grid; grid-template-columns: minmax(0, .74fr) minmax(0, 1fr); gap: 44px; align-items: start; border-top: 1px solid #dbe3ef; }
.lp-content { display: grid; gap: 16px; }
.lp-content-media { border-radius: 12px; overflow: hidden; border: 1px solid #dbe3ef; background: #fff; }
.lp-content-media img { width: 100%; height: min(36vw, 360px); min-height: 220px; object-fit: cover; }
.lp-content h2, .lp-process h2, .lp-faq h2, .lp-form-copy h2, .lp-agenda h2 { margin: 0; color: var(--lp-ink); font-size: clamp(28px, 4vw, 46px); line-height: 1.08; letter-spacing: 0; font-weight: 660; text-wrap: balance; }
.lp-checklist { display: grid; gap: 10px; padding: 0; margin: 18px 0 0; list-style: none; }
.lp-checklist li { display: grid; grid-template-columns: 28px minmax(0, 1fr); gap: 10px; align-items: start; color: #334155; line-height: 1.45; }
.lp-checklist li::before { content: ""; width: 18px; height: 18px; margin-top: 2px; border-radius: 50%; background: color-mix(in srgb, var(--lp-accent) 82%, #fff); }
.lp-process-grid, .lp-agenda-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; margin-top: 24px; }
.lp-step { border-radius: 8px; background: color-mix(in srgb, var(--lp-primary) 7%, #fff); padding: 22px; min-height: 150px; display: grid; gap: 12px; align-content: space-between; }
.lp-step span { color: var(--lp-primary); font-size: 13px; font-weight: 750; }
.lp-proof-box { border: 1px solid #dbe3ef; border-radius: 8px; background: #fff; padding: 26px; display: grid; gap: 16px; }
.lp-proof-box blockquote { margin: 0; color: var(--lp-ink); font-size: clamp(22px, 3vw, 34px); line-height: 1.18; font-weight: 620; }
.lp-proof-box cite { color: #64748b; font-style: normal; }
.lp-faq details { border: 1px solid #dbe3ef; border-radius: var(--lp-radius); background: #fff; overflow: hidden; }
.lp-faq details + details { margin-top: 12px; }
.lp-faq summary { padding: 16px 18px; font-weight: 650; cursor: pointer; list-style: none; color: var(--lp-ink); }
.lp-faq summary::-webkit-details-marker { display: none; }
.lp-faq details[open] summary { border-bottom: 1px solid #e2e8f0; }
.lp-faq details p { margin: 0; padding: 0 18px 16px; color: color-mix(in srgb, var(--lp-ink) 72%, #64748b); line-height: 1.55; }
.lp-faq-grid { display: grid; grid-template-columns: 1fr; gap: 12px; margin-top: 24px; max-width: 760px; }
.lp-faq article { border: 1px solid #dbe3ef; border-radius: var(--lp-radius); background: #fff; padding: 16px 18px; display: grid; gap: 8px; }
[data-aurora-form-slot] { width: 100%; max-width: 100%; min-width: 0; }
.lead-form { width: 100%; border: 1px solid #dbe3ef; border-radius: 8px; background: #fff; padding: 24px; display: grid; gap: 16px; box-shadow: 0 18px 55px rgba(15, 23, 42, .1); color: #0f172a; }
.lead-form h2 { margin: 0; font-size: 24px; line-height: 1.2; font-weight: 680; }
.lead-form .form-header { display: grid; gap: 6px; }
.lead-form label { display: grid; gap: 6px; color: #334155; font-size: 14px; line-height: 1.25; }
.lead-form input, .lead-form textarea { width: 100%; min-height: 44px; border: 1px solid #cbd5e1; border-radius: 8px; padding: 10px 12px; background: #f8fafc; color: #0f172a; font: inherit; resize: vertical; }
.lead-form textarea { min-height: 108px; }
.lead-form button { min-height: 48px; border: 0; border-radius: 8px; background: var(--lp-primary); color: #fff; font: inherit; font-weight: 700; padding: 0 18px; cursor: pointer; }
.form-error { border-radius: 8px; background: #fff1f2; color: #be123c; padding: 10px 12px; font-size: 14px; }
.success-state { display: grid; gap: 10px; text-align: left; }
.success-state i { color: var(--lp-accent); font-size: 28px; }
.lp-faq-grid { display: grid; grid-template-columns: 1fr; gap: 12px; margin-top: 24px; max-width: 760px; }
.lp-faq article { border: 1px solid #dbe3ef; border-radius: var(--lp-radius); background: #fff; padding: 16px 18px; display: grid; gap: 8px; }
@media (max-width: 860px) {
  .lp-brand, .lp-section-inner { width: min(100% - 28px, 680px); }
  .lp-section { width: 100%; }
  .lp-brand { align-items: center; flex-direction: row; }
  .lp-hero, .lp-split, .lp-form-section .lp-section-inner, .lp-grid, .lp-process-grid, .lp-agenda-grid, .lp-faq-grid { grid-template-columns: 1fr; }
  .lp-hero { min-height: auto; padding-top: 26px; }
  .lp-section { padding: 38px 0; }
  .lp-hero h1 { font-size: clamp(34px, 13vw, 48px); }
  .lp-hero p, .lp-section-lede { font-size: 17px; }
  .lp-visual { min-height: 250px; padding: 20px; }
  .lp-visual img, .lp-content-media img { height: 260px; min-height: 220px; }
  .lp-button { width: 100%; }
}
@media (max-width: 520px) {
  .lp-page { background: linear-gradient(180deg, color-mix(in srgb, var(--lp-primary) 8%, var(--lp-bg)), var(--lp-bg) 32%); }
  .lp-brand { width: calc(100% - 28px); padding-top: 18px; gap: 10px; }
  .lp-logo { max-width: 120px; max-height: 54px; }
  .lp-section { width: 100%; padding: 32px 0; }
  .lp-section-inner { width: calc(100% - 28px); }
  .lp-hero { gap: 22px; }
  .lp-hero h1 { font-size: clamp(32px, 12vw, 46px); line-height: 1.03; }
  .lp-content h2, .lp-process h2, .lp-faq h2, .lp-form-copy h2, .lp-agenda h2 { font-size: clamp(24px, 8vw, 32px); }
  .lp-visual { min-height: 220px; padding: 18px; border-radius: 10px; }
  .lp-visual img, .lp-content-media img { height: 220px; min-height: 200px; }
  .lead-form { padding: 18px; }
}
`.trim();
}

export function renderLandingTemplateCssPrimer(templateKey: LandingTemplateV2Key | string): string {
    const key = normalizeTemplateV2Key(templateKey);
    if (key === 'consultation') {
        return `
.lp-page--consultation .lp-hero { width: 100%; display:grid; grid-template-columns:1fr 1fr; gap:var(--lp-space-md); min-height:75vh; align-items:center; padding:var(--lp-space-lg) clamp(16px, 4vw, 32px); background:linear-gradient(145deg,var(--lp-primary) 0%,color-mix(in srgb,var(--lp-primary) 70%,#000) 100%); }
.lp-page--consultation .lp-hero.lp-hero--has-bg { background-image: linear-gradient(145deg, color-mix(in srgb, var(--lp-primary) 82%, #000), color-mix(in srgb, var(--lp-primary) 52%, #000)), var(--lp-hero-bg-image, none); background-size: cover; background-position: center; }
.lp-page--consultation .lp-hero-copy { color:#fff; }
.lp-page--consultation .lp-hero h1, .lp-page--consultation .lp-hero p, .lp-page--consultation .lp-hero .lp-lede { color:#fff; }
.lp-page--consultation .lp-kicker { background:rgba(255,255,255,.14); border-color:rgba(255,255,255,.28); color:#fff; }
.lp-page--consultation .lp-proof-panel { background:rgba(255,255,255,.12); border-radius:var(--lp-radius); padding:var(--lp-space-md); border-left:4px solid var(--lp-accent); color:#fff; }
.lp-page--consultation .lp-benefit-list { display:grid; grid-template-columns:repeat(3,1fr); gap:var(--lp-space-md); }
.lp-page--consultation .lp-benefit-card { background:#fff; border:1px solid #dbe3ef; border-radius:var(--lp-radius); box-shadow:var(--lp-shadow); padding:var(--lp-space-md); }
.lp-page--consultation .lp-trust-note { background:var(--lp-primary); color:#fff; text-align:center; padding:var(--lp-space-lg) var(--lp-space-md); }
.lp-page--consultation .lp-process-steps { display:flex; gap:var(--lp-space-md); counter-reset:step; }
.lp-page--consultation .lp-step { flex:1; position:relative; padding-top:var(--lp-space-md); border:1px solid #dbe3ef; border-radius:var(--lp-radius); background:#fff; padding:var(--lp-space-md); }
.lp-page--consultation .lp-step::before { content:counter(step); counter-increment:step; display:flex; align-items:center; justify-content:center; width:40px; height:40px; border-radius:50%; background:var(--lp-accent); color:#fff; font-weight:700; margin-bottom:var(--lp-space-sm); }
@media (max-width:640px) { .lp-page--consultation .lp-hero, .lp-page--consultation .lp-benefit-list, .lp-page--consultation .lp-process-steps { grid-template-columns:1fr; flex-direction:column; } }
`.trim();
    }
    if (key === 'offer') {
        return `
.lp-page--offer .lp-hero { display:grid; grid-template-columns:3fr 2fr; gap:var(--lp-space-md); align-items:start; padding:var(--lp-space-lg) var(--lp-space-md); background:var(--lp-primary); color:#fff; }
.lp-page--offer .lp-benefit-card, .lp-page--offer .lp-card { border:1px solid #dbe3ef; border-radius:var(--lp-radius); box-shadow:var(--lp-shadow); }
`.trim();
    }
    if (key === 'event') {
        return `
.lp-page--event .lp-hero { background:var(--lp-primary); color:#fff; text-align:center; padding:var(--lp-space-xl) var(--lp-space-md); }
.lp-page--event .lp-agenda-item { border:1px solid #dbe3ef; border-radius:var(--lp-radius); box-shadow:var(--lp-shadow); }
`.trim();
    }
    return `
.lp-page--lead-magnet .lp-hero { display:grid; grid-template-columns:1fr 1fr; gap:var(--lp-space-md); align-items:center; padding:var(--lp-space-lg) var(--lp-space-md); background:linear-gradient(135deg,var(--lp-primary) 0%,color-mix(in srgb,var(--lp-primary) 80%,var(--lp-accent)) 100%); color:#fff; }
.lp-page--lead-magnet .lp-use-case-card { border:1px solid #dbe3ef; border-radius:var(--lp-radius); box-shadow:var(--lp-shadow); }
`.trim();
}

export function buildLandingBaseCss(project: LandingProjectV2): string {
    return [renderLandingProjectCss(project), renderLandingTemplateCssPrimer(project.templateKey)].join('\n');
}

export function resolveLandingPrimaryColor(primaryColor?: string, accentColor?: string, fallback = '#3157e6'): string {
    const primary = safeColor(primaryColor, '');
    if (primary && colorLuminance(primary) < 0.58) return primary;
    const accent = safeColor(accentColor, '');
    if (accent && colorLuminance(accent) < 0.58) return accent;
    return fallback;
}

export function isLandingPrimaryColorUnreadable(primaryColor?: string): boolean {
    const primary = safeColor(primaryColor, '');
    if (!primary) return false;
    return colorLuminance(primary) >= 0.58;
}

export function renderPreviewDocument(project: LandingProjectV2): string {
    return [
        '<!doctype html>',
        '<html>',
        '<head>',
        '<meta charset="utf-8" />',
        '<meta name="viewport" content="width=device-width, initial-scale=1" />',
        `<style>${renderLandingProjectCss(project)}</style>`,
        '</head>',
        '<body>',
        renderLandingProjectHtml(project, 'preview'),
        '</body>',
        '</html>'
    ].join('');
}

export function renderPreviewForm(form: LeadLandingFormConfig): string {
    const fields = form.fields.map((field) => renderPreviewField(field)).join('');
    return `<form class="lead-form lead-form--preview">
  <div class="form-header"><h2>${escapeHtml(form.title || 'Richiedi informazioni')}</h2></div>
  ${fields}
  <button type="button">${escapeHtml(form.submitLabel || 'Invia richiesta')}</button>
</form>`;
}

export function sanitizeLandingHtml(html: string): string {
    const withoutForbidden = (html || '')
        .replace(/<script\b[\s\S]*?<\/script>/gi, '')
        .replace(/<style\b[\s\S]*?<\/style>/gi, '')
        .replace(/<iframe\b[\s\S]*?<\/iframe>/gi, '')
        .replace(/<object\b[\s\S]*?<\/object>/gi, '')
        .replace(/<embed\b[\s\S]*?<\/embed>/gi, '')
        .replace(/<form\b[\s\S]*?<\/form>/gi, '')
        .replace(
            /<\/?(html|head|body|meta|link|script|style|iframe|object|embed|form|input|textarea|button|select|option)[^>]*>/gi,
            ''
        )
        .replace(/\s(on[a-z]+|style|srcdoc)\s*=\s*(".*?"|'.*?'|[^\s>]+)/gi, '');

    const mainMatch = withoutForbidden.match(/<main\b[\s\S]*<\/main>/i);
    let next = (mainMatch ? mainMatch[0] : `<main class="lp-page">${withoutForbidden}</main>`).trim();
    const slotMatches = next.match(/data-aurora-form-slot/gi) ?? [];
    if (slotMatches.length === 0) {
        next = next.replace(/<\/main>\s*$/i, `${AURORA_FORM_SLOT}</main>`);
    } else if (slotMatches.length > 1) {
        let seen = 0;
        next = next.replace(/<[^>]*data-aurora-form-slot[^>]*>(?:<\/[^>]+>)?/gi, (match) => {
            seen += 1;
            return seen === 1 ? match : '';
        });
    }
    return next;
}

export function sanitizeLandingCss(css: string): string {
    return (css || '')
        .replace(/<\/?style[^>]*>/gi, '')
        .replace(/@import[^;]+;/gi, '')
        .replace(/expression\s*\([^)]*\)/gi, '')
        .replace(/javascript:/gi, '')
        .replace(/behavior\s*:/gi, '')
        .replace(/-moz-binding\s*:/gi, '')
        .trim();
}

export type LandingThemeTokens = {
    primaryColor?: string;
    accentColor?: string;
    backgroundColor?: string;
    textColor?: string;
    fontFamily?: string;
};

type CssBlock = { prelude: string; body: string };

function splitCssBlocks(css: string): CssBlock[] {
    const blocks: CssBlock[] = [];
    let preludeStart = 0;
    let bodyStart = -1;
    let depth = 0;
    let quote: string | null = null;

    for (let index = 0; index < css.length; index += 1) {
        const char = css[index];
        const previous = css[index - 1];
        if ((char === '"' || char === "'") && previous !== '\\') {
            quote = quote === char ? null : (quote ?? char);
            continue;
        }
        if (quote) continue;

        if (char === '{') {
            if (depth === 0) bodyStart = index + 1;
            depth += 1;
        } else if (char === '}') {
            depth -= 1;
            if (depth === 0 && bodyStart >= 0) {
                blocks.push({
                    prelude: css.slice(preludeStart, bodyStart - 1).trim(),
                    body: css.slice(bodyStart, index).trim()
                });
                preludeStart = index + 1;
                bodyStart = -1;
            }
        }
    }

    return blocks.filter((block) => block.prelude && block.body);
}

function normalizeCssPrelude(prelude: string): string {
    return prelude.replace(/\s+/g, ' ').trim();
}

export function buildLandingCssTokens(theme: LandingThemeTokens): string {
    const primary = resolveLandingPrimaryColor(theme.primaryColor, theme.accentColor);
    return `:root {
  --lp-primary: ${primary};
  --lp-accent: ${safeColor(theme.accentColor, '#16b981')};
  --lp-bg: ${safeColor(theme.backgroundColor, '#f7f9fc')};
  --lp-ink: ${safeColor(theme.textColor, '#0f172a')};
  --lp-font: ${safeFont(theme.fontFamily)};
  --lp-radius: 12px;
  --lp-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
  --lp-shadow-lg: 0 12px 48px rgba(0, 0, 0, 0.16);
  --lp-space-sm: 16px;
  --lp-space-md: 32px;
  --lp-space-lg: 64px;
  --lp-space-xl: 96px;
}`;
}

export function mergeLandingThemeIntoCss(css: string, theme: LandingThemeTokens): string {
    const tokens = buildLandingCssTokens(theme);
    const withoutRootTokens = (css || '').replace(/:root\s*\{[^}]*--lp-primary[\s\S]*?\}\s*/gi, '');
    return sanitizeLandingCss(`${tokens}\n${withoutRootTokens}`.trim());
}

function parseCssProperties(body: string): Map<string, string> {
    const properties = new Map<string, string>();
    for (const declaration of body.split(';')) {
        const separator = declaration.indexOf(':');
        if (separator < 0) continue;
        const key = declaration.slice(0, separator).trim();
        const value = declaration.slice(separator + 1).trim();
        if (key && value) properties.set(key, value);
    }
    return properties;
}

function mergeCssBlockBodies(baseBody: string, patchBody: string): string {
    const merged = parseCssProperties(baseBody);
    for (const [key, value] of parseCssProperties(patchBody)) {
        merged.set(key, value);
    }
    return [...merged.entries()].map(([key, value]) => `${key}: ${value}`).join('; ');
}

export function mergeLandingCssPreservingRules(base: string, patch: string): string {
    if (!patch.trim()) return base;
    if (!base.trim()) return patch;

    const mergedByPrelude = new Map<string, string>();
    for (const block of splitCssBlocks(base)) {
        mergedByPrelude.set(normalizeCssPrelude(block.prelude), block.body);
    }
    for (const block of splitCssBlocks(patch)) {
        const key = normalizeCssPrelude(block.prelude);
        const existing = mergedByPrelude.get(key);
        mergedByPrelude.set(key, existing ? mergeCssBlockBodies(existing, block.body) : block.body);
    }

    const merged = [...mergedByPrelude.entries()].map(([prelude, body]) => `${prelude} { ${body} }`).join('\n');
    return sanitizeLandingCss(merged);
}

export function mergeLandingCssAfterEdit(base: string, patch: string): string {
    if (!patch.trim()) return base;
    const patchBlocks = splitCssBlocks(patch);
    const baseBlocks = splitCssBlocks(base);
    if (patchBlocks.length >= Math.max(10, Math.floor(baseBlocks.length * 0.55))) {
        return patch;
    }
    return mergeLandingCssPreservingRules(base, patch);
}

export function ensureLandingTemplateClass(html: string, templateKey: string): string {
    const doc = new DOMParser().parseFromString(html || '', 'text/html');
    const main = doc.querySelector('main');
    if (!main) return html;
    main.classList.add('lp-page');
    const modifier = `lp-page--${templateKey}`;
    if (templateKey && !main.classList.contains(modifier)) {
        main.classList.add(modifier);
    }
    return main.outerHTML;
}

export function normalizeLandingPublicHtml(html: string): string {
    const doc = new DOMParser().parseFromString(html || '', 'text/html');
    const main = doc.querySelector('main');
    if (!main) return html;

    main.querySelectorAll<HTMLElement>('.lp-brand-row, .lp-brand, header.lp-brand').forEach((row) => {
        const logo = row.querySelector('img.lp-brand-logo, img.lp-logo');
        row.querySelectorAll('.lp-brand-name').forEach((name) => {
            if (logo) name.remove();
        });
    });

    main.querySelectorAll<HTMLAnchorElement>('a').forEach((anchor) => {
        const className = anchor.className || '';
        const isCta =
            className.includes('lp-cta') ||
            className.includes('lp-button') ||
            className.includes('cta') ||
            anchor.closest('.lp-hero, .lp-form-section, .lp-trust-note, .lp-contact-panel') !== null;
        if (!isCta) return;
        anchor.setAttribute('href', '#form');
        anchor.removeAttribute('target');
        anchor.removeAttribute('rel');
    });

    const formSection =
        main.querySelector<HTMLElement>('[data-section="form"]') ??
        main.querySelector<HTMLElement>('.lp-form-section, .lp-contact-panel');
    if (formSection) {
        formSection.id = 'form';
        const inner = formSection.querySelector<HTMLElement>('.lp-section-inner') ?? formSection;
        const slot = inner.querySelector<HTMLElement>('[data-aurora-form-slot]');
        if (slot && !inner.querySelector('.lp-form-copy')) {
            const copy = doc.createElement('div');
            copy.className = 'lp-form-copy';
            const movable = [...inner.childNodes].filter((node) => node !== slot);
            movable.forEach((node) => copy.appendChild(node));
            if (copy.childNodes.length > 0) {
                inner.insertBefore(copy, slot);
            }
        }
    }

    main.querySelectorAll('.lp-page-footer').forEach((footer) => footer.remove());

    return main.outerHTML;
}

export type LandingPublicFooterConfig = {
    legalLine?: string;
    privacyPolicyUrl?: string;
    termsOfServiceUrl?: string;
};

function readCustomFieldPath(source: Record<string, unknown>, path: string): unknown {
    return path.split('.').reduce<unknown>((acc, key) => {
        if (acc && typeof acc === 'object' && !Array.isArray(acc) && key in (acc as Record<string, unknown>)) {
            return (acc as Record<string, unknown>)[key];
        }
        return undefined;
    }, source);
}

export function landingPublicFooterFromCustomFields(customFields: Record<string, unknown>): LandingPublicFooterConfig {
    const landingBuilder = customFields['landingBuilder'];
    const footer =
        landingBuilder && typeof landingBuilder === 'object' && !Array.isArray(landingBuilder)
            ? (landingBuilder as Record<string, unknown>)['publicFooter']
            : undefined;
    if (footer && typeof footer === 'object' && !Array.isArray(footer)) {
        const source = footer as Record<string, unknown>;
        const nested = {
            legalLine: readFooterConfigString(source['legalLine']),
            privacyPolicyUrl: readFooterConfigString(source['privacyPolicyUrl']),
            termsOfServiceUrl: readFooterConfigString(source['termsOfServiceUrl'])
        };
        if (nested.legalLine || nested.privacyPolicyUrl || nested.termsOfServiceUrl) {
            return nested;
        }
    }

    return {
        legalLine: readFooterConfigString(readCustomFieldPath(customFields, 'landingBuilder.publicFooter.legalLine')),
        privacyPolicyUrl: readFooterConfigString(
            readCustomFieldPath(customFields, 'landingBuilder.publicFooter.privacyPolicyUrl')
        ),
        termsOfServiceUrl: readFooterConfigString(
            readCustomFieldPath(customFields, 'landingBuilder.publicFooter.termsOfServiceUrl')
        )
    };
}

export function landingPublicFooterFromSeo(seo?: Record<string, unknown> | null): LandingPublicFooterConfig {
    if (!seo) return {};
    const footer = seo['publicFooter'];
    if (!footer || typeof footer !== 'object' || Array.isArray(footer)) return {};
    const source = footer as Record<string, unknown>;
    return {
        legalLine: readFooterConfigString(source['legalLine']),
        privacyPolicyUrl: readFooterConfigString(source['privacyPolicyUrl']),
        termsOfServiceUrl: readFooterConfigString(source['termsOfServiceUrl'])
    };
}

/**
 * Resolves the public landing footer shown on published pages.
 * Priority: API `footer` (from tenant custom fields at request time) > `seo.publicFooter` snapshot saved on publish.
 * The seo snapshot is a transitional fallback when the API does not yet expose footer.
 */
export function resolveLandingPublicFooter(
    page?: {
        footer?:
            | LandingPublicFooterConfig
            | { legalLine?: string | null; privacyPolicyUrl?: string | null; termsOfServiceUrl?: string | null }
            | null;
        seo?: Record<string, unknown> | null;
    } | null
): LandingPublicFooterConfig {
    const fromApi = page?.footer ?? {};
    const fromSeo = landingPublicFooterFromSeo(page?.seo);
    return {
        legalLine: readFooterConfigString(fromApi.legalLine) ?? fromSeo.legalLine,
        privacyPolicyUrl: readFooterConfigString(fromApi.privacyPolicyUrl) ?? fromSeo.privacyPolicyUrl,
        termsOfServiceUrl: readFooterConfigString(fromApi.termsOfServiceUrl) ?? fromSeo.termsOfServiceUrl
    };
}

export function landingPublicFooterFromTenantCustomFieldsJson(
    raw: string | null | undefined
): LandingPublicFooterConfig {
    if (!raw?.trim()) return {};
    try {
        const parsed = JSON.parse(raw) as unknown;
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
        return landingPublicFooterFromCustomFields(parsed as Record<string, unknown>);
    } catch {
        return {};
    }
}

export function renderLandingFooterHtml(config: LandingPublicFooterConfig, auroraMarketingUrl: string): string {
    const legalLine = config.legalLine?.trim() ?? '';
    const privacyUrl = config.privacyPolicyUrl?.trim() ?? '';
    const termsUrl = config.termsOfServiceUrl?.trim() ?? '';
    const links = [
        privacyUrl
            ? `<a href="${escapeAttribute(privacyUrl)}" target="_blank" rel="noopener noreferrer">Privacy</a>`
            : '',
        termsUrl
            ? `<a href="${escapeAttribute(termsUrl)}" target="_blank" rel="noopener noreferrer">Termini di servizio</a>`
            : ''
    ]
        .filter(Boolean)
        .join('');

    return `<footer class="lp-page-footer" data-section="footer">
  <div class="lp-page-footer-inner">
    ${legalLine ? `<p class="lp-page-footer-legal">${escapeHtml(legalLine)}</p>` : ''}
    ${links ? `<nav class="lp-page-footer-links" aria-label="Collegamenti legali">${links}</nav>` : ''}
    <a class="lp-page-footer-aurora" href="${escapeAttribute(auroraMarketingUrl)}" target="_blank" rel="noopener noreferrer">Powered by Aurora CRM</a>
  </div>
</footer>`;
}

/** Keep in sync with public-lead-landing-page-footer.css (editor iframe preview). */
export const LANDING_FOOTER_CSS = `
.lp-page-footer {
  width: 100%;
  margin-top: auto;
  padding: 28px 0 34px;
  border-top: 1px solid #dbe3ef;
  background: #f1f5f9;
  background: color-mix(in srgb, var(--lp-ink, #0f172a) 3%, var(--lp-bg, #f7f9fc));
  color: #64748b;
  color: color-mix(in srgb, var(--lp-ink, #0f172a) 68%, #64748b);
}
.lp-page-footer-inner {
  width: min(1120px, calc(100% - 32px));
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 20px;
  font-size: 13px;
  line-height: 1.45;
}
.lp-page-footer-legal {
  margin: 0;
  flex: 1 1 240px;
}
.lp-page-footer-links {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
}
.lp-page-footer-links a,
.lp-page-footer-aurora {
  color: #64748b;
  color: color-mix(in srgb, var(--lp-ink, #0f172a) 72%, #64748b);
  text-decoration: none;
}
.lp-page-footer-links a:hover,
.lp-page-footer-aurora:hover {
  color: var(--lp-primary, #2563eb);
  text-decoration: underline;
}
.lp-page-footer-aurora {
  margin-left: auto;
  font-size: 12px;
  opacity: 0.82;
}
@media (max-width: 760px) {
  .lp-page-footer-inner {
    width: calc(100% - 28px);
    flex-direction: column;
    align-items: flex-start;
  }
  .lp-page-footer-aurora {
    margin-left: 0;
  }
}
`.trim();

export function landingFooterCss(): string {
    return LANDING_FOOTER_CSS;
}

function readFooterConfigString(value: unknown): string | undefined {
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

export function defaultAuroraMarketingUrl(production: boolean): string {
    return production ? 'https://www.auroracrm.it' : 'https://www.dev.auroracrm.it';
}

export function landingEditorSystemCss(): string {
    return `
            .lp-brand-row {
                width: 100%;
                display: flex;
                align-items: center;
                gap: 14px;
                margin: 0 0 clamp(28px, 5vw, 64px);
                padding: 0;
                min-width: 0;
            }
            .lp-brand-logo {
                width: auto;
                max-width: clamp(104px, 16vw, 180px);
                max-height: 64px;
                object-fit: contain;
                flex: 0 0 auto;
            }
            .lp-brand-name {
                color: var(--lp-ink);
                font-size: 15px;
                line-height: 1.2;
                font-weight: 750;
                min-width: 0;
            }
            .lp-hero--has-bg {
                background-repeat: no-repeat;
                background-size: cover;
                background-position: center;
            }
            .lp-hero-image img,
            .lp-visual img {
                width: 100%;
                object-fit: cover;
            }
            .lp-text-primary { color: var(--lp-primary); }
            .lp-text-accent { color: var(--lp-accent); }
            .lp-text-white { color: #fff; }
            .lp-text-lg { font-size: 1.15em; line-height: 1.5; }
            .lp-text-sm { font-size: 0.92em; line-height: 1.45; }
            .lp-strong { font-weight: 700; }
            .lp-em { font-style: italic; }
            @media (max-width: 760px) {
                .lp-brand-row {
                    margin-bottom: 28px;
                    gap: 10px;
                }
                .lp-brand-logo {
                    max-width: 132px;
                    max-height: 54px;
                }
            }
        `;
}

function renderBlock(project: LandingProjectV2, block: LandingBlockV2, formHtml: string): string {
    switch (block.type) {
        case 'hero':
            return renderHero(project, block);
        case 'benefits':
            return renderBenefits(block);
        case 'content':
            return renderContent(block);
        case 'proof':
            return renderProof(block);
        case 'process':
            return renderProcess(block);
        case 'agenda':
            return renderAgenda(block);
        case 'faq':
            return renderFaq(block);
        case 'form':
            return renderFormBlock(block, formHtml);
        default:
            return '';
    }
}

function renderBrandHeader(project: LandingProjectV2): string {
    const brandName = project.theme.brandName || project.name;
    const logoUrl = project.theme.logoUrl?.trim();
    const logo = logoUrl
        ? `<img class="lp-logo" src="${escapeAttribute(logoUrl)}" alt="" />`
        : `<span class="lp-logo-fallback">${escapeHtml(initials(brandName))}</span>`;
    const nameMarkup = logoUrl ? '' : `<strong class="lp-brand-name">${escapeHtml(brandName)}</strong>`;
    return `<header class="lp-brand">${logo}${nameMarkup}</header>`;
}

function renderHero(project: LandingProjectV2, block: LandingBlockV2): string {
    const visual = block.imageUrl
        ? `<figure class="lp-visual"><img src="${escapeAttribute(block.imageUrl)}" alt="" /></figure>`
        : `<aside class="lp-visual"><span>${escapeHtml(block.eyebrow || project.name)}</span><strong>${escapeHtml(block.ctaLabel || project.form.submitLabel || project.name)}</strong><p>${escapeHtml(heroVisualCopy(project.templateKey))}</p></aside>`;

    return `<section class="lp-section lp-hero" data-ai-section="${escapeAttribute(block.id)}">
  <div class="lp-hero-copy">
    ${block.eyebrow ? `<p class="lp-kicker">${escapeHtml(block.eyebrow)}</p>` : ''}
    <h1>${escapeHtml(block.title)}</h1>
    ${block.body ? `<p>${escapeHtml(block.body)}</p>` : ''}
    ${block.ctaLabel ? `<a class="lp-button" href="#form">${escapeHtml(block.ctaLabel)}</a>` : ''}
  </div>
  ${visual}
</section>`;
}

function renderBenefits(block: LandingBlockV2): string {
    return `<section class="lp-section" data-ai-section="${escapeAttribute(block.id)}">
  <div class="lp-content">
    <h2>${escapeHtml(block.title)}</h2>
    ${block.body ? `<p class="lp-section-lede">${escapeHtml(block.body)}</p>` : ''}
  </div>
  <div class="lp-grid">${(block.items ?? []).map(renderCard).join('')}</div>
</section>`;
}

function renderContent(block: LandingBlockV2): string {
    const media = block.imageUrl
        ? `<figure class="lp-content-media"><img src="${escapeAttribute(block.imageUrl)}" alt="" /></figure>`
        : '';
    return `<section class="lp-section lp-split" data-ai-section="${escapeAttribute(block.id)}">
  <div class="lp-content">
    ${block.eyebrow ? `<p class="lp-section-kicker">${escapeHtml(block.eyebrow)}</p>` : ''}
    <h2>${escapeHtml(block.title)}</h2>
  </div>
  <div class="lp-content">
    ${media}
    ${block.body ? `<p>${escapeHtml(block.body)}</p>` : ''}
    ${block.items?.length ? `<ul class="lp-checklist">${block.items.map((item) => `<li>${escapeHtml(item.title)}</li>`).join('')}</ul>` : ''}
  </div>
</section>`;
}

function renderProof(block: LandingBlockV2): string {
    const media = block.imageUrl
        ? `<figure class="lp-content-media"><img src="${escapeAttribute(block.imageUrl)}" alt="" /></figure>`
        : '';
    return `<section class="lp-section lp-split" data-ai-section="${escapeAttribute(block.id)}">
  <div class="lp-content">
    <h2>${escapeHtml(block.title)}</h2>
    ${block.body ? `<p>${escapeHtml(block.body)}</p>` : ''}
  </div>
  <div class="lp-proof-box">
    ${media}
    <blockquote>${escapeHtml(block.quote || block.body || block.title)}</blockquote>
    ${block.quoteAuthor ? `<cite>${escapeHtml(block.quoteAuthor)}</cite>` : ''}
  </div>
</section>`;
}

function renderProcess(block: LandingBlockV2): string {
    return `<section class="lp-section lp-process" data-ai-section="${escapeAttribute(block.id)}">
  <h2>${escapeHtml(block.title)}</h2>
  <div class="lp-process-grid">${(block.items ?? []).map(renderStep).join('')}</div>
</section>`;
}

function renderAgenda(block: LandingBlockV2): string {
    return `<section class="lp-section lp-agenda" data-ai-section="${escapeAttribute(block.id)}">
  <h2>${escapeHtml(block.title)}</h2>
  ${block.body ? `<p class="lp-section-lede">${escapeHtml(block.body)}</p>` : ''}
  <div class="lp-agenda-grid">${(block.items ?? []).map(renderStep).join('')}</div>
</section>`;
}

function renderFaq(block: LandingBlockV2): string {
    return `<section class="lp-section lp-faq" data-ai-section="${escapeAttribute(block.id)}">
  <h2>${escapeHtml(block.title)}</h2>
  <div class="lp-faq-grid">${(block.items ?? []).map(renderFaqItem).join('')}</div>
</section>`;
}

function renderFormBlock(block: LandingBlockV2, formHtml: string): string {
    return `<section id="form" class="lp-section lp-form-section" data-ai-section="${escapeAttribute(block.id)}">
  <div class="lp-form-copy">
    ${block.eyebrow ? `<p class="lp-section-kicker">${escapeHtml(block.eyebrow)}</p>` : ''}
    <h2>${escapeHtml(block.title)}</h2>
    ${block.body ? `<p class="lp-section-lede">${escapeHtml(block.body)}</p>` : ''}
  </div>
  ${formHtml}
</section>`;
}

function renderCard(item: LandingBlockItemV2): string {
    return `<article class="lp-card"><strong>${escapeHtml(item.title)}</strong>${item.body ? `<p>${escapeHtml(item.body)}</p>` : ''}</article>`;
}

function renderStep(item: LandingBlockItemV2, index: number): string {
    return `<article class="lp-step"><span>${String(index + 1).padStart(2, '0')}</span><strong>${escapeHtml(item.title)}</strong>${item.body ? `<p>${escapeHtml(item.body)}</p>` : ''}</article>`;
}

function renderFaqItem(item: LandingBlockItemV2): string {
    return `<article><h3>${escapeHtml(item.title)}</h3>${item.body ? `<p>${escapeHtml(item.body)}</p>` : ''}</article>`;
}

function renderPreviewField(field: LeadLandingFormField): string {
    const required = field.required ? '<span class="required-star">*</span>' : '';
    const control =
        field.type === 'textarea'
            ? '<textarea rows="4" disabled></textarea>'
            : `<input type="${escapeAttribute(field.type === 'tel' ? 'tel' : field.type || 'text')}" disabled />`;
    return `<label><span>${escapeHtml(field.label)}${required}</span>${control}</label>`;
}

function heroVisualCopy(templateKey: LandingTemplateV2Key): string {
    if (templateKey === 'event') return 'Registrazione, dettagli e follow-up raccolti in un unico passaggio.';
    if (templateKey === 'offer') return 'Una richiesta breve per avviare una valutazione più concreta.';
    if (templateKey === 'lead-magnet') return 'Contenuto utile e ricontatto ordinato dopo la richiesta.';
    return 'Un primo contatto chiaro per capire il percorso più adatto.';
}

function safeColor(value: string | undefined, fallback: string): string {
    return /^#[0-9a-f]{6}$/i.test(value || '') ? (value as string) : fallback;
}

function colorLuminance(hex: string): number {
    const channels = [hex.slice(1, 3), hex.slice(3, 5), hex.slice(5, 7)].map((part) => {
        const channel = parseInt(part, 16) / 255;
        return channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

function safeFont(value: string | undefined): string {
    return value?.replace(/[<>{}]/g, '') || 'Inter, system-ui, sans-serif';
}

function initials(value: string): string {
    return value
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('');
}

function escapeHtml(value: string | undefined): string {
    return (value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function escapeAttribute(value: string | undefined): string {
    return escapeHtml(value).replace(/`/g, '&#96;');
}
